import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

interface User {
  id: string;
  email: string;
  nation: string;
  nationCode?: string;
  username?: string;
  displayName?: string;
  role: 'NATION' | 'EU' | 'ADMIN' | 'EXCON' | 'PLANNER' | 'HUB';
  flagImage?: string;
  containerAddress?: string;
  isSimulated: boolean;
  // Multi-role fields
  isNation?: boolean;
  isEU?: boolean;
  isAdmin?: boolean;
  isExcon?: boolean;
  isPlanner?: boolean;
  isHub?: boolean;
  isReadOnly?: boolean;
  mustChangePassword?: boolean;
  lastLoginAt?: string;
}

/** Merged roles: union of originalUser + impersonated user roles */
interface EffectiveRoles {
  isAdmin: boolean;
  isExcon: boolean;
  isPlanner: boolean;
  isHub: boolean;
  isNation: boolean;
  isEU: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  // Read-only instance
  isReadOnlyInstance: boolean;
  // Impersonation
  isImpersonating: boolean;
  originalUser: User | null;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => void;
  /** When impersonating, union of original + impersonated user roles; otherwise just user roles */
  effectiveRoles: EffectiveRoles;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Read-only instance state
  const [isReadOnlyInstance, setIsReadOnlyInstance] = useState(false);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [originalToken, setOriginalToken] = useState<string | null>(null);

  useEffect(() => {
    // Load token from localStorage
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedOriginalToken = localStorage.getItem('originalToken');
    const storedOriginalUser = localStorage.getItem('originalUser');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }

    // Restore impersonation state
    if (storedOriginalToken && storedOriginalUser) {
      setIsImpersonating(true);
      setOriginalToken(storedOriginalToken);
      setOriginalUser(JSON.parse(storedOriginalUser));
    }

    setLoading(false);

    // Check if this is a read-only instance
    fetch(`${API_URL}/api/instance-info`)
      .then(r => r.json())
      .then(data => { if (data.readOnlyInstance) setIsReadOnlyInstance(true); })
      .catch(() => {}); // ignore errors
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: email.trim().toLowerCase(),
        password
      });

      const { token: newToken, user: newUser } = response.data;

      setToken(newToken);
      setUser(newUser);

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // Clear any impersonation state on fresh login
      setIsImpersonating(false);
      setOriginalUser(null);
      setOriginalToken(null);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('originalUser');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
    delete axios.defaults.headers.common['Authorization'];
  };

  const impersonate = async (userId: string) => {
    try {
      // Always use the original EXCON/ADMIN token for the impersonate call,
      // so switching directly from one simulated user to another works
      const authToken = originalToken || token;
      const response = await axios.post(`${API_URL}/api/auth/impersonate/${userId}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const { token: newToken, user: impersonatedUser } = response.data;

      // Save current (real) user before switching
      if (!isImpersonating) {
        // First impersonation - save the real EXCON user
        setOriginalUser(user);
        setOriginalToken(token);
        localStorage.setItem('originalToken', token!);
        localStorage.setItem('originalUser', JSON.stringify(user));
      }
      // If already impersonating, keep the original EXCON user

      // Switch to impersonated user
      setToken(newToken);
      setUser(impersonatedUser);
      setIsImpersonating(true);

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Impersonation failed');
    }
  };

  // Compute effective roles: union of original user + impersonated user roles
  const effectiveRoles: EffectiveRoles = {
    isAdmin: !!(user?.isAdmin || (isImpersonating && originalUser?.isAdmin)),
    isExcon: !!(user?.isExcon || (isImpersonating && originalUser?.isExcon)),
    isPlanner: !!(user?.isPlanner || (isImpersonating && originalUser?.isPlanner)),
    isHub: !!(user?.isHub || (isImpersonating && originalUser?.isHub)),
    isNation: !!(user?.isNation || (isImpersonating && originalUser?.isNation)),
    isEU: !!(user?.isEU || (isImpersonating && originalUser?.isEU)),
  };

  const stopImpersonating = () => {
    if (!originalToken || !originalUser) return;

    // Restore original EXCON user
    setToken(originalToken);
    setUser(originalUser);
    setIsImpersonating(false);

    localStorage.setItem('token', originalToken);
    localStorage.setItem('user', JSON.stringify(originalUser));
    axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;

    // Clear impersonation storage
    setOriginalUser(null);
    setOriginalToken(null);
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
  };

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, loading, isReadOnlyInstance,
      isImpersonating, originalUser, impersonate, stopImpersonating, effectiveRoles
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
